import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Edit } from 'lucide-react';

import { updateGroup } from '@/lib/firebase/queries/groups';

import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import type { Group } from '@/types/support';

interface EditGroupDialogProps {
  group: Group & { children: any[]; level?: number; aggregatedMembers?: number };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Receives the saved fields so the caller can show them without a reload. */
  onSuccess?: (changes: { name: string; description: string; updatedAt: Date }) => void;
}

export function EditGroupDialog({ 
  group, 
  open, 
  onOpenChange, 
  onSuccess
}: EditGroupDialogProps) {
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || '');
  const [isLoading, setIsLoading] = useState(false);

  // Check if this is a section (parent group) or regular group
  const isSection = group.groupType === 'parent';


  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description || '');
    }
  }, [open, group]);

  // Description is optional (AddGroupDialog creates groups without one).
  const handleSave = async () => {
    if (!name.trim() || !currentUser) return;

    const changes = {
      name: name.trim(),
      description: (description || '').trim(),
      updatedAt: new Date(),
    };

    try {
      setIsLoading(true);

      const updateSuccess = await updateGroup(group.id, {
        ...changes,
        categories: group.categories || [],
        isPrivate: group.isPrivate,
      });

      if (!updateSuccess) {
        toast.error(isSection
          ? t('dashboard.manager.groups.updateSectionError')
          : t('dashboard.manager.groups.updateError')
        );
        return;
      }

      toast.success(isSection
        ? t('dashboard.manager.groups.updateSectionSuccess')
        : t('dashboard.manager.groups.updateSuccess')
      );

      onSuccess?.(changes);
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error(isSection
        ? t('dashboard.manager.groups.updateSectionError')
        : t('dashboard.manager.groups.updateError')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="max-w-md">
         <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'} dir={dir}>
           <DialogTitle className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
             <Edit className="h-5 w-5" />
             {isSection ? t('dashboard.manager.groups.editSectionTitle') : t('dashboard.manager.groups.editGroupTitle')}
           </DialogTitle>
         </DialogHeader>
        
        <div className="space-y-4" dir={dir}>
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="itemName" className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {isSection ? t('dashboard.manager.groups.section') : t('dashboard.manager.groups.table.name')}
            </Label>
            <Input
              id="itemName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={isSection ? t('dashboard.manager.groups.section') : t('dashboard.manager.groups.table.name')}
              className={dir === 'rtl' ? 'text-right' : 'text-left'}
              dir={dir}
            />
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="itemDescription" className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              {t('dashboard.manager.groups.description')}
            </Label>
            <Textarea
              id="itemDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('dashboard.manager.groups.description')}
              rows={3}
              className={dir === 'rtl' ? 'text-right' : 'text-left'}
              dir={dir}
            />
          </div>
        </div>

         <DialogFooter className={dir === 'rtl' ? 'flex-row-reverse' : ''} dir={dir}>
           <Button variant="outline" onClick={() => onOpenChange(false)}>
             {t('common.cancel')}
           </Button>
           <Button 
             onClick={handleSave} 
             disabled={!name.trim() || isLoading}
             className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
           >
             {isLoading && <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />}
             {t('common.save')}
           </Button>
         </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 